export const primaryDemoCta = 'Book a demo';
export const tryCouplingCta = 'Contact us to try Coupling';
export const monthlyPrice = '$0/month';
export const mintingExample = 'Issue $10,000 worth of rewards for about $1.20.';
export const bonusVoucherCopy = 'Ask how merchant rewards and vouchers can support your launch.';
// Coupling POS leads every message. Square/Clover are a short, secondary mention for
// merchants who will not give up an existing register. Clover is NOT live (approval pending).
export const ownPosHeadline = 'Coupling POS runs your entire checkout';
export const ownPosFeatureList =
  'custom catalogs and workflows, online orders paid up front or at pickup, appointments, recurring subscriptions, and custom tipping with tip pooling';
export const ownPosAction = 'run your entire checkout in Coupling POS';
// Third-person variant for sentences about merchants ("merchants … run their …").
export const ownPosActionThirdPerson = 'run their entire checkout in Coupling POS';
export const demoTopics =
  'Coupling POS checkout, online orders, appointments, subscriptions, and tips';
export const existingPosQuestion = 'Keeping your current register?';
export const existingPosAnswer = 'Square works beside Coupling; Clover is coming soon.';
export const existingPosNote = `${existingPosQuestion} ${existingPosAnswer}`;
export const productSummary = `${ownPosHeadline} on the phone you already have: ${ownPosFeatureList}.`;
export const heroPosChips = ['Coupling POS', 'Online orders & appointments'];
export const heroPosChoice = 'Coupling POS · Tap to Pay or an optional Stripe reader';
export const merchantsPayFact = 'Free merchant access — no monthly subscription dues';
export const freeForUsersFact = 'No monthly fee for core customer access';
export const noTransactionFeesFact = 'No Coupling fee on each sale';
export const paySubscriptionInTokensFact =
  'Minting costs are shown before you issue tokens';
export const merchantConnectionExample = {
  cap: '$100',
  summary:
    'Merchant A can choose to accept up to $100 of Merchant B rewards, then bring that value back to Merchant B.',
};
export const merchantPartnershipShowcaseHeading =
  'Grow local partnerships without opening the network blindly.';
export const merchantPartnershipShowcaseDescription =
  'Start with checkout and your own rewards, then add a partner when the relationship makes sense and cap how much of their value you accept.';

export const heroHeadline =
  'Your own rewards and gift cards, accepted across local shops — right in your POS.';
export const heroSubhead =
  'Create reward points or gift cards for your shop in minutes. Customers earn them with you and can spend them at other WNC merchants who choose to accept them — an open network of local businesses, not a single-store punch card. It all runs in Coupling POS on the phone you already carry, with Tap to Pay or an optional Stripe reader.';

export type CouplingStoryStep = {
  step: string;
  title: string;
  description: string;
};

export const couplingStoryHeading =
  'One shop’s rewards, spendable across the neighborhood.';
export const couplingStoryIntro =
  'Most loyalty programs lock value inside one store. Coupling is open loop: your rewards and gift cards can travel to the local merchants you partner with, and theirs can bring new customers to you.';
export const couplingStorySteps: CouplingStoryStep[] = [
  {
    step: '1',
    title: 'Create your own rewards or gift cards',
    description:
      'Set up reward points or gift cards under your shop’s name in minutes. You pay a small, upfront creation cost — shown before you confirm — and there is no Coupling fee on each sale.',
  },
  {
    step: '2',
    title: 'Customers earn with you and spend nearby',
    description:
      'Paid sales send rewards you already created to the customer. Partner merchants who accept your rewards — up to a limit they set — let customers spend them down the street, and theirs can bring new faces to you.',
  },
  {
    step: '3',
    title: 'All in the POS you already carry',
    description:
      'Ring sales, mix cash, card, and rewards on one ticket, and see what’s covered — in Coupling POS on your phone with Tap to Pay, or an optional Stripe reader. Catalogs, online orders, appointments, subscriptions, and tips run there too.',
  },
];

export const hardwareFraming = 'Tap to Pay on your phone, or an optional Stripe reader.';
export const hardwareFramingLong =
  'Take cards with Tap to Pay on the phone you already have, or add an optional Stripe reader available through Coupling.';

// POS and payment workflow section: Coupling POS leads; Square/Clover are one small card.
export type PosCapability = { title: string; description: string };
export type PosWorkflowStepCopy = { title: string; description: string; label: string };

export const posWorkflowEyebrow = 'POS and payment workflow';
export const posWorkflowHeading =
  'We can build your entire checkout. Coupling POS runs the whole sale.';
export const posWorkflowIntro =
  'Custom catalogs and workflows, online orders paid up front or at pickup, appointments, recurring subscriptions, and tipping — all in Coupling POS on the phone you already carry. Take cards with Tap to Pay or an optional Stripe reader, mix cash, card, and rewards on one ticket, and the customer’s reward is sent automatically.';
export const posWorkflowChips = [
  'Coupling POS',
  'Tap to Pay on your phone',
  'Optional Stripe readers',
  'Tips and tip pooling',
];
export const posCapabilitiesEyebrow = 'Everything at the counter and online';
export const posCapabilitiesHeading = 'One checkout system, built around how you sell.';
export const posCapabilities: PosCapability[] = [
  {
    title: 'Custom catalogs and workflows',
    description: 'Your items, sizes, add-ons, and order steps, set up the way your shop works.',
  },
  {
    title: 'Online orders, paid up front or at pickup',
    description: 'Customers order from your web menu and pay now or when they pick up.',
  },
  {
    title: 'Embeddable web menu',
    description: 'Drop your catalog and checkout onto your own website.',
  },
  {
    title: 'Appointments and booking',
    description: 'Customers pick a time; you see it next to the rest of the shop.',
  },
  {
    title: 'Recurring subscriptions',
    description: 'Memberships and repeat plans billed on a schedule.',
  },
  {
    title: 'Custom tipping and tip pooling',
    description: 'Set your own tip options and pool tips across your servers.',
  },
  {
    title: 'Bar tabs and running checks',
    description: 'Open a tab, add to it through the night, and close it in one payment.',
  },
  {
    title: 'Send a bill, guest pays',
    description: 'Text or email a bill; the customer pays from their phone, no app needed.',
  },
  {
    title: 'Mixed tender',
    description: 'Split one sale across cash, card, and rewards and see what is still due.',
  },
  {
    title: 'Catering minimums',
    description: 'Set order minimums and lead times for catering and large orders.',
  },
];
export const posWorkflowSteps: PosWorkflowStepCopy[] = [
  {
    title: 'Start the sale in Coupling POS',
    description:
      'Ring the sale on your phone. Tap to Pay on the phone you already carry, or an optional Stripe reader.',
    label: 'Charge generated',
  },
  {
    title: 'Mix cash, card, and rewards',
    description:
      'The shopper pays with card (including Tap to Pay), cash, rewards, or a mix. The app shows how much is covered before the sale closes.',
    label: 'Customer payment',
  },
  {
    title: 'Close the remaining balance',
    description:
      'Coupling shows the rewards already applied and the amount still due, so you can finish the sale on your phone.',
    label: 'Mixed tender',
  },
  {
    title: 'The reward is sent automatically',
    description:
      'When the sale completes, Coupling sends the customer a reward from the rewards you already created — no extra step at the counter.',
    label: 'Reward sent',
  },
];
export const posIntegrationCards: PosCapability[] = [
  {
    title: 'Your choice of hardware',
    description:
      'Tap to Pay on your phone, or an optional Stripe reader available through Coupling.',
  },
  {
    title: 'What the shopper sees',
    description:
      'Rewards reduce the balance before checkout, and the new reward appears after the sale closes.',
  },
  {
    title: existingPosQuestion,
    description: existingPosAnswer,
  },
];
export const posScreenshotsNote =
  'Screenshots below show the live Coupling POS on a phone and tablet.';
